import {
  all,
  call,
  put,
  race,
  take,
  takeLatest,
  delay
} from 'redux-saga/effects';
import axios from 'axios';
import { toastr } from 'react-redux-toastr';

import { LOCATION_CHANGE } from 'connected-react-router';
import {
  GET_INSTANCES,
  GET_INSTANCES_STATUSES,
  GET_INSTANCES_STATUSES_SUCCESS,
  UPDATE_INSTANCE
} from 'actions/instances/constants';
import {
  getInstancesSuccess,
  getInstancesError,
  getInstancesStatuses,
  getInstancesStatusesSuccess,
  getInstancesStatusesError,
  updateInstanceSuccess,
  updateInstanceError
} from 'actions/instances';
import { hideModal } from 'actions/modal';

import normaliseError from 'utils/normaliseError';

import {
  API_URL,
  BURN_IN_BEHAVIOURS,
  HTTP_VERBS,
  INSTANCES_STATUSES_POLL_INTERVAL
} from 'utils/constants';

/* Project instances */
export function* getInstancesSaga({ projectId }) {
  const requestUrl = `${API_URL}/projects/${projectId}/instances`;
  const opts = {
    method: HTTP_VERBS.GET,
    url: requestUrl
  };

  try {
    const response = yield call(axios, opts);
    yield put(getInstancesSuccess(projectId, response.data.data));
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(getInstancesError(projectId, transformedError));
  }
}

export function* getInstancesWatcher() {
  yield takeLatest(GET_INSTANCES, getInstancesSaga);
}

/* Project instances status */
export function* getInstancesStatusesSaga({ projectId }) {
  const requestUrl = `${API_URL}/projects/${projectId}/instances_status`;
  const opts = {
    method: HTTP_VERBS.GET,
    url: requestUrl
  };

  try {
    const response = yield call(axios, opts);
    yield put(getInstancesStatusesSuccess(projectId, response.data.data));
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(getInstancesStatusesError(projectId, transformedError));
  }
}

export function* getInstancesStatusesWatcher() {
  yield takeLatest(GET_INSTANCES_STATUSES, getInstancesStatusesSaga);
}

export function* getInstancesStatusesWithDelay(projectId) {
  yield delay(INSTANCES_STATUSES_POLL_INTERVAL);
  yield put(getInstancesStatuses(projectId));
}

export function* getInstancesStatusesPollController() {
  while (true) {
    const { projectId } = yield take(GET_INSTANCES_STATUSES_SUCCESS);

    yield race({
      instanceStatuses: call(getInstancesStatusesWithDelay, projectId),
      locationChange: take(LOCATION_CHANGE)
    });
  }
}

/* Update project instance */
export function* updateInstanceSaga({
  projectId,
  instanceId,
  instance,
  actions
}) {
  const {
    alias,
    burnInBehaviour,
    burnInEnabled,
    burnInFontSize,
    burnInHorizontalPlacement,
    burnInOpacity,
    burnInRandomInterval,
    burnInTemplate,
    burnInVerticalPlacement
  } = instance;

  const defaults = {
    audio_layout: instance.audioChannels,
    audio_quality: instance.audioQuality,
    input_bitdepth: instance.inputBitdepth,
    inputChroma: instance.inputChroma,
    decklink_input: instance.decklinkInput,
    dynamic_range: instance.dynamicRange,
    output_bitdepth: instance.outputBitdepth,
    video_quality: instance.videoQuality
  };

  const amendedInstance = {
    alias,
    overlay_settings: {
      burnin: {
        enabled: burnInEnabled,
        font: {
          size: burnInFontSize
        },
        opacity: burnInOpacity,
        position: {
          horizontal_placement: burnInHorizontalPlacement,
          random: {
            enabled: burnInBehaviour === BURN_IN_BEHAVIOURS.RANDOM,
            interval_seconds: burnInRandomInterval
          },
          scrolling: {
            horizontal: burnInBehaviour === BURN_IN_BEHAVIOURS.SCROLL
          },
          vertical_placement: burnInVerticalPlacement
        },
        template: burnInTemplate
      }
    },
    stream_settings_defaults: defaults,
    available_stream_settings: {}
  };

  const requestUrl = `${API_URL}/projects/${projectId}/instances/${instanceId}`;
  const opts = {
    method: HTTP_VERBS.PUT,
    url: requestUrl,
    data: JSON.stringify(amendedInstance)
  };

  try {
    const response = yield call(axios, opts);
    yield put(updateInstanceSuccess(projectId, instanceId, response.data.data));
    yield put(hideModal());
    yield call(toastr.success, 'FlexBox settings updated');
  } catch (error) {
    const transformedError = normaliseError(error);
    yield put(updateInstanceError(projectId, instanceId, transformedError));
    if (actions) {
      yield call(actions.setErrors, {
        errors: transformedError.errors,
        description: transformedError.description
      });
      yield call(actions.setSubmitting, false);
    }
  }
}

export function* updateInstanceWatcher() {
  yield takeLatest(UPDATE_INSTANCE, updateInstanceSaga);
}

export default function* rootSaga() {
  yield all([
    call(getInstancesWatcher),
    call(getInstancesStatusesWatcher),
    call(getInstancesStatusesPollController),
    call(updateInstanceWatcher)
  ]);
}
